import { useMemo, useState, useEffect } from 'react';
import { useAuthContext } from '@contexts/AuthContext';
import { useLanguage } from '@contexts/LanguageContext';
import api from '@services/api';

/**
 * Hook to get the users that the current user can borrow for
 * @returns {Object} { borrowOptions: Array, loading: boolean, error: string|null }
 */
export const useBorrowForUserOptions = () => {
    const { user } = useAuthContext();
    const { t } = useLanguage();
    const [userNames, setUserNames] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Extract target user IDs from BORROW_FOR_USER permissions
    const targetUserIds = useMemo(() => {
        if (!user?.permissions) return [];

        return user.permissions
            .filter(permission =>
                permission.permission_type === 'BORROW_FOR_USER' &&
                permission.is_active &&
                permission.target_user_id
            )
            .map(permission => permission.target_user_id);
    }, [user?.permissions]);

    // Fetch user names for the target user IDs
    useEffect(() => {
        if (targetUserIds.length === 0) return;

        const fetchUserNames = async () => {
            setLoading(true);
            setError(null);

            try {
                const promises = targetUserIds.map(async (userId) => {
                    if (userNames[userId]) return; // Already fetched

                    try {
                        const response = await api.users.getById(userId);
                        return {
                            userId,
                            name: response.data.name || response.data.email || `User ${userId}`
                        };
                    } catch (err) {
                        console.warn(`Failed to fetch user ${userId}:`, err);
                        return {
                            userId,
                            name: `User ${userId}`
                        };
                    }
                });

                const results = await Promise.all(promises);
                const newUserNames = {};

                results.forEach(result => {
                    if (result) {
                        newUserNames[result.userId] = result.name;
                    }
                });

                setUserNames(prev => ({ ...prev, ...newUserNames }));
            } catch (err) {
                setError('Failed to fetch user information');
                console.error('Error fetching user names:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchUserNames();
    }, [targetUserIds]);

    // Create borrow options array
    const borrowOptions = useMemo(() => {
        const options = [];

        targetUserIds.forEach(userId => {
            options.push({
                key: `user_${userId}`,
                label: userNames[userId] || `${t('common.loading')}...`,
                targetUserId: userId,
                isSelf: false,
                description: t('borrowing.borrowFor') || 'Borrow for this user'
            });
        });

        return options;
    }, [targetUserIds, userNames, t]);

    return {
        borrowOptions,
        loading,
        error,
        hasMultipleOptions: borrowOptions.length > 1,
        targetUserIds
    };
};

export default useBorrowForUserOptions;
